import * as Sentry from '@sentry/vue'
import { isScoredSlideType } from '@ahaslides-product/plugins-api'
import { resolveSameOriginBase } from '@/utils/url.utils'
import { allSlideTypes } from '@/constant/slideTypes'

// A built-in slide type identifier (e.g. `ranking`, `poll`, `content-v2`). Used to
// keep a developer slug from hijacking a native slide of the same name.
const _isBuiltInType = (type) => allSlideTypes.some((s) => s && s.type === type)

export const EMBED_APP_FLAG = 'AHA-42142-embed-app'

const SET_MANIFESTS = 'SET_MANIFESTS'
const SET_FULL_SCREEN = 'SET_FULL_SCREEN'

const _state = {
  manifests: [],
  fullScreen: false,
}

// Manifest assets (icon, previewUrl) may be specified as a path relative to
// the plugin's baseUrl. Resolve them so consumers always see an absolute URL.
export const resolveAssetUrl = (asset, baseUrl) => {
  if (typeof asset !== 'string' || asset === '') return asset
  if (asset.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(asset)) return asset
  if (typeof baseUrl !== 'string' || baseUrl === '') return asset
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const path = asset.startsWith('/') ? asset : `/${asset}`
  return `${base}${path}`
}

// AHA-47349-presenter-can-create-new-slide-type — resolve a developer slide type
// (one the user created, from the user-manifests endpoint) by its stable marker.
// These live in the slideMarketplace catalogue but are NOT `marketplace/`-typed,
// so the marketplace lookup never matches them. Flag off / not found => null.
const _resolveDeveloperSlideType = (type, rootGetters) => {
  if (!rootGetters || !rootGetters['featureToggles/canUseCreateNewSlideType']) return null
  const marketplaceSlideTypes = rootGetters['slideMarketplace/marketplaceSlideTypes'] || []
  // A developer slide's stored `slide.type` may be the pageId (the entry's `type`,
  // e.g. `f876…`) OR the bare slug (`tinder`) — slides created before/after the
  // pageId-as-type change carry different forms — optionally `marketplace/`-prefixed.
  // The pageId `type` is random and safe to match unconditionally; the slug is
  // developer-chosen, so it must NOT match a bare slug that collides with a
  // built-in slide type (`ranking`, `poll`, …) — that would hijack every native
  // slide of that type. So the slug leg is gated on `!_isBuiltInType(bare)`.
  const bare = String(type).replace(/^marketplace\//, '')
  const slugMatchable = !_isBuiltInType(bare)
  // A published type belongs to ANY creator and its slug is free text, so it
  // matches only by its exact `type` (the stable publishedKey), never by slug.
  return marketplaceSlideTypes.find((s) => s && s.isDeveloperSlideType
    && (s.type === type || s.type === bare
      || (!s.isPublishedSlideType && slugMatchable && (s.slug === type || s.slug === bare)))) || null
}

const _getters = {
  isEnabled: (_s, _g, _rs, rootGetters) => {
    const variant = rootGetters['featureToggles/getVariant'](EMBED_APP_FLAG)
    return !!variant?.enabled
  },
  manifests: (state) => state.manifests,
  isFullScreen: (state, getters) => getters.isEnabled && state.fullScreen,
  manifestById: (state) => (id) => state.manifests.find((m) => m.id === id),
  appsByContext: (state) => (context) => {
    const tabs = []
    state.manifests.forEach((app) => {
      (app.staticTabs || []).forEach((tab) => {
        if (tab.context === context) {
          tabs.push({
            ...tab,
            appId: app.id,
            name: tab.name || app.name,
            icon: resolveAssetUrl(tab.icon || app.icon, app.baseUrl),
            app,
          })
        }
      })
    })
    return tabs
  },
  customSlideTypes: (state) => {
    const types = []
    state.manifests.forEach((app) => {
      (app.slideTypes || []).forEach((slideType) => {
        if (slideType && slideType.type) {
          types.push({
            ...slideType,
            icon: resolveAssetUrl(slideType.icon, app.baseUrl),
            previewUrl: resolveAssetUrl(slideType.previewUrl, app.baseUrl),
            appId: app.id,
            app,
          })
        }
      })
    })
    return types
  },
  isScoredSlide: (_, getters) => (slide) => {
    if (!slide) return false
    return isScoredSlideType(getters.customSlideTypeByType(slide.type))
  },

  customSlideTypeByType: (_, getters, __, rootGetters) => (type) => {
    if (!type) return null
    const found = getters.customSlideTypes.find((s) => s.type === type)
    if (found) return found

    if (rootGetters && rootGetters['featureToggles/canUseNewSlideMarketPlace']) {
      const marketplaceSlideTypes = rootGetters['slideMarketplace/marketplaceSlideTypes']
      if (marketplaceSlideTypes) {
        // AHA-47349: exclude developer slide types here — their `source` is spread
        // from the backend and could be `'market'`, which would otherwise route them
        // through this branch (with marketplace setting-defaults) before the
        // developer branch and its pageId-OR-slug fallback.
        const marketplaceSlide = marketplaceSlideTypes.find((s) => s.type === type
          && !s.isDeveloperSlideType
          && (s.type.startsWith('marketplace/') || s.source === 'market'))
        if (marketplaceSlide) {
          return {
            ...marketplaceSlide,
            plugin: true,
            setting: {
              enableQuestionTitle: false,
              enableQuestionDescription: false,
              enableTimeLimit: false,
              enableStopSubmitssionSetting: false,
              enableHideResultSetting: false,
              enableQuestionImage: false,
              enableVoteCount: false,
              enableLabelOtherSetting: false,
              enableMultipleSubmission: false,
              enableFullScreen: false,
              ...(marketplaceSlide.setting || {}),
            },
          }
        }
      }
    }

    // AHA-47349-presenter-can-create-new-slide-type — resolved LAST so a genuine
    // `marketplace/<slug>` (matched above) can never be shadowed by a developer
    // entry of the same type. `plugin: true` is forced for shape-parity with the
    // marketplace branch; the entry's own `setting` (already complete) is kept.
    // URLs are already absolutized in developerManifest. See _resolveDeveloperSlideType.
    const developerSlide = _resolveDeveloperSlideType(type, rootGetters)
    if (developerSlide) return { ...developerSlide, plugin: true }

    return null
  },
}

// Optional per-env override for the aha-workspace app's baseUrl, so a sandbox
// or local build can point the workspace iframe at a specific host (e.g. the
// dev workspace app) without needing an Unleash payload edit. Empty / unset
// leaves the manifest untouched.
const _workspaceAppUrlOverride = () => {
  const raw = process.env.VUE_APP_WORKSPACE_APP_URL
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null
}

const _applyWorkspaceUrlOverride = (manifests) => {
  const override = _workspaceAppUrlOverride()
  if (!override) return manifests
  return manifests.map((m) => (m?.id === 'aha-workspace' ? { ...m, baseUrl: override } : m))
}

// Trailing slash enforced so EmbedAppContainer's new URL(relativePath, baseUrl)
// join keeps the path segment.
const _resolveSameOriginBaseUrlsFromManifest = (manifests) => {
  if (typeof window === 'undefined') return manifests
  return manifests.map((m) => (typeof m?.baseUrl === 'string' && m.baseUrl !== ''
    ? { ...m, baseUrl: resolveSameOriginBase(m.baseUrl).replace(/\/?$/, '/') }
    : m))
}

const _parseManifests = (payload) => {
  if (!payload?.value) return []
  try {
    const parsed = typeof payload.value === 'string' ? JSON.parse(payload.value) : payload.value
    if (!Array.isArray(parsed)) return []
    const cleaned = parsed.filter((item) => item && typeof item === 'object')
    return _resolveSameOriginBaseUrlsFromManifest(_applyWorkspaceUrlOverride(cleaned))
  } catch (error) {
    Sentry.captureException(error, { tags: { source: 'embed-app.module.js' } })
    return []
  }
}

const actions = {
  loadManifests({ commit, rootGetters }) {
    const variant = rootGetters['featureToggles/getVariant'](EMBED_APP_FLAG)
    if (!variant?.enabled) {
      commit(SET_MANIFESTS, [])
      return
    }

    const manifests = _parseManifests(variant.payload)
    commit(SET_MANIFESTS, manifests)
  },
}

/* eslint-disable no-param-reassign */
const mutations = {
  [SET_MANIFESTS](state, manifests) {
    state.manifests = manifests
  },
  [SET_FULL_SCREEN](state, fullScreen) {
    state.fullScreen = fullScreen
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
